import type { components as AssessmentApi } from './generated/assessment';
import type { components as CaseApi } from './generated/case';
import type { components as IdentityApi } from './generated/identity';
import type { AuditEvent } from './client';
import type { ActivityEvent, AdminClient, AdminUser } from './adminMock';
import type { CaseLanguage, CaseRecord } from './caseStore';
import { DIMENSIONS } from './mock';
import type { Lens, Question } from './types';

/**
 * Adapters map the generated OpenAPI contract types (source of truth, owned by
 * the backend) into the frontend view-models. Because the inputs are the
 * generated `components["schemas"]` types, any drift between the contract and
 * the frontend surfaces here as a TypeScript error at build time — this is the
 * enforcement point that keeps back and front aligned.
 */

type MatrixResponse = AssessmentApi['schemas']['AssessmentMatrixResponse'];
type CreateOracleUserRequest = IdentityApi['schemas']['CreateOracleUserRequest'];
type ClientAccountUpsertRequest = IdentityApi['schemas']['ClientAccountUpsertRequest'];
type UpdateClientAccountRequest = IdentityApi['schemas']['UpdateClientAccountRequest'];

export function buildClientUsername(clientName: string): string {
  const slug = clientName
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_');

  return `cx_${slug || 'client'}`;
}

/**
 * User form → identity contract. The explicit return type makes any drift
 * between the AdminUser view-model and CreateOracleUserRequest a build error.
 */
export function toCreateOracleUserRequest(
  user: Omit<AdminUser, 'id'> & { password: string }
): CreateOracleUserRequest {
  return {
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    phone: user.phone,
    privilege: user.privilege,
    role: user.role,
    area: user.area,
    country: user.country,
    assessmentIds: user.assessmentIds,
    mustChangePassword: user.mustChangePassword,
    password: user.password,
    status: user.status
  };
}

type OracleUserResponse = IdentityApi['schemas']['OracleUserResponse'];
type UpdateOracleUserRequest = IdentityApi['schemas']['UpdateOracleUserRequest'];

/** AdminUser edits → identity PATCH body (email/phone are not editable here). */
export function toUpdateOracleUserRequest(
  u: Pick<AdminUser, 'privilege' | 'role' | 'area' | 'country' | 'status' | 'mustChangePassword' | 'assessmentIds'>
): UpdateOracleUserRequest {
  return {
    privilege: u.privilege,
    role: u.role,
    area: u.area,
    country: u.country,
    assessmentIds: u.assessmentIds,
    status: u.status,
    mustChangePassword: u.mustChangePassword
  };
}

type ClientAccountResponse = IdentityApi['schemas']['ClientAccountResponse'];

/** client-account response → AdminClient view-model (org name resolved by caller). */
export function clientAccountToAdminClient(a: ClientAccountResponse, orgName: string): AdminClient {
  return {
    id: a.id,
    clientId: a.clientId,
    firstName: a.firstName,
    lastName: a.lastName,
    email: a.email,
    phone: a.phone,
    client: orgName,
    country: a.country,
    industry: a.industry,
    status: a.status,
    mustChangePassword: a.mustChangePassword,
    assessmentIds: a.assessmentIds,
    lastSeenAt: a.lastSeenAt ?? null
  };
}

export function toUpdateClientAccountRequest(
  client: Pick<
    AdminClient,
    'firstName' | 'lastName' | 'phone' | 'country' | 'industry' | 'status' | 'mustChangePassword' | 'assessmentIds'
  >
): UpdateClientAccountRequest {
  return {
    firstName: client.firstName,
    lastName: client.lastName,
    phone: client.phone,
    country: client.country,
    industry: client.industry,
    status: client.status,
    mustChangePassword: client.mustChangePassword,
    assessmentIds: client.assessmentIds
  };
}

/** Formats an ISO lastSeenAt as `YYYY-MM-DD HH:mm`, or null when never seen. */
export function formatLastSeen(iso: string | null | undefined): string | null {
  if (!iso) return null;
  return iso.replace('T', ' ').slice(0, 16);
}

/** audit-service event → Activity log view-model (actor/entity flattened for the table). */
export function auditEventToActivityEvent(e: AuditEvent): ActivityEvent {
  const when = e.eventTime.replace('T', ' ').slice(0, 19);
  const actor = e.actorId ?? e.actorType;
  const entity = e.entityId ? `${e.entityType} · ${e.entityId}` : e.entityType;
  return { id: e.id, when, actor, type: e.eventType, entity };
}

/** identity contract response → AdminUser view-model. */
export function oracleUserToAdminUser(u: OracleUserResponse): AdminUser {
  return {
    id: u.id,
    firstName: u.firstName,
    lastName: u.lastName,
    email: u.email,
    phone: u.phone,
    country: u.country,
    area: u.area,
    role: u.role,
    privilege: u.privilege,
    status: u.status,
    mustChangePassword: u.mustChangePassword,
    assessmentIds: u.assessmentIds,
    lastSeenAt: u.lastSeenAt ?? null
  };
}

// ---- Case adapters ----

type CaseResponse = CaseApi['schemas']['CaseResponse'];
type ClientResponse = CaseApi['schemas']['ClientResponse'];
type CreateCaseRequest = CaseApi['schemas']['CreateCaseRequest'];

const STATE_LABEL: Record<string, string> = {
  DRAFT: 'Draft',
  IN_PROGRESS: 'In Progress',
  UNDER_REVIEW: 'Under Review',
  UNDER_APPROVAL: 'Under Approval',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled'
};

export function stateCodeToLabel(code: string): string {
  return STATE_LABEL[code] ?? code;
}

export function labelToStateCode(label: string): string {
  const found = Object.entries(STATE_LABEL).find(([, v]) => v === label);
  return found ? found[0] : label.toUpperCase().replace(/ /g, '_');
}

/** Backend case (full CaseResponse) → CaseRecord. Client name resolved from the
 *  clients map; enabled dimension names mapped back to their ids. */
export function caseSummaryToRecord(
  detail: CaseResponse,
  clientsById: Map<string, ClientResponse>
): CaseRecord {
  const client = clientsById.get(detail.clientId);
  return {
    caseId: detail.caseId,
    name: detail.name,
    client: client?.name ?? detail.clientId,
    country: detail.country || client?.country || '',
    language: detail.language as CaseLanguage,
    state: stateCodeToLabel(detail.stateCode),
    enabledDimensions: DIMENSIONS.filter((d) => detail.enabledDimensions.includes(d.name)).map(
      (d) => d.id
    ),
    stateHistory: [],
    createdAt: detail.createdAt ? detail.createdAt.slice(0, 10) : '',
    updatedAt: '',
    archived: detail.archived ?? false
  };
}

/** New-case form → create request. clientId, the question-bank version and the
 *  facilitator (session user) are resolved beforehand. */
export function toCreateCaseRequest(
  draft: { name: string; language: CaseLanguage; country?: string; industry?: string },
  clientId: string,
  questionBankVersionId: string,
  assignedOracleUserIds: string[],
  enabledDimensions: string[]
): CreateCaseRequest {
  return {
    name: draft.name,
    clientId,
    language: draft.language,
    enabledDimensions,
    assignedOracleUserIds,
    stateCode: 'DRAFT',
    questionBankVersionId,
    country: draft.country,
    industry: draft.industry
  };
}

/**
 * Client form → identity contract. `clientId` is resolved from the org (client
 * name → id) at call time; the org itself lives in case-service's GET /clients.
 */
export function toClientAccountUpsertRequest(
  client: Omit<AdminClient, 'id'> & { password: string },
  clientId: string
): ClientAccountUpsertRequest {
  return {
    clientId,
    firstName: client.firstName,
    lastName: client.lastName,
    email: client.email,
    phone: client.phone,
    country: client.country,
    industry: client.industry,
    password: client.password,
    mustChangePassword: client.mustChangePassword,
    status: client.status,
    assessmentIds: client.assessmentIds
  };
}

/** Picks the requested language from a localized `{ es, en, pt }` string map. */
function localized(map: { [key: string]: string }, lang: string): string {
  return map[lang] ?? map.es ?? Object.values(map)[0] ?? '';
}

/** Flattens the contract's lens-grouped matrix into the flat Question[] the UI uses. */
export function matrixToQuestions(res: MatrixResponse, lang = 'es'): Question[] {
  const questions: Question[] = [];
  for (const group of res.lensGroups) {
    for (const q of group.questions) {
      questions.push({
        id: q.questionId,
        dimension: res.dimension,
        capability: res.capability,
        // Question.persona carries the ROLE for display/scoring; the person's
        // name + personaId are tracked in the assessment page state.
        persona: res.role,
        lens: group.lens as Lens,
        text: localized(q.questionText, lang),
        options: q.answerOptions.map((o) => ({
          value: o.value as 0 | 1 | 2 | 3 | 4,
          text: localized(o.text, lang)
        })),
        answer: q.currentAnswer?.selectedValue ?? null
      });
    }
  }
  return questions;
}
